import React from "react";
import {
    Tractor, Gauge, Fuel, Calendar, Clock, Lamp, Palette, Home, Layers,
    Hammer, Package, PaintBucket, Truck, BadgeCheck, Ruler,
    Gem, Scale, Sparkles, Shield, Users,
} from "lucide-react";
import { hasProductExtras } from "./ProductMetaExtras";

function Row({ Icon, label, children, testid }) {
    // v79.181 — Item 15: global rule — never render an attribute row
    // when the value is empty. Belt-and-braces even if a specific
    // section forgot to guard the row (e.g. `meta.value && <Row …>`).
    const raw = Array.isArray(children) ? children.join("").trim() : String(children ?? "").trim();
    if (!raw) return null;
    return (
        <div data-testid={testid} className="flex items-start gap-2.5 text-sm">
            <Icon className="w-4 h-4 text-aduku-navy/55 mt-0.5 shrink-0" />
            <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-wider font-bold text-aduku-navy/55">{label}</div>
                <div className="text-aduku-navy font-semibold mt-0.5">{children}</div>
            </div>
        </div>
    );
}

export default function ProductMetaExtrasDisplay({ listing }) {
    const meta = listing?.meta || {};
    const cat = listing?.category;
    if (!hasProductExtras(cat)) return null;

    if (cat === "farm-equipments") {
        const hasAny = meta.machine_type || meta.power_hp || meta.fuel_type || meta.year || meta.hours_used || meta.drive || meta.attachments;
        if (!hasAny) return null;
        return (
            <section data-testid="farm-eq-display" className="bg-white border border-aduku-border rounded-xl p-5 mt-4">
                <div className="flex items-center gap-2 mb-3">
                    <Tractor className="w-4 h-4 text-aduku-navy" />
                    <h3 className="font-display font-bold text-lg text-aduku-navy">Equipment details</h3>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                    {meta.machine_type && <Row Icon={Tractor} label="Type" testid="fe-machine">{meta.machine_type}</Row>}
                    {meta.power_hp && <Row Icon={Gauge} label="Power" testid="fe-power">{meta.power_hp} HP</Row>}
                    {meta.fuel_type && <Row Icon={Fuel} label="Fuel" testid="fe-fuel">{meta.fuel_type}</Row>}
                    {meta.year && <Row Icon={Calendar} label="Year" testid="fe-year">{meta.year}</Row>}
                    {meta.hours_used && <Row Icon={Clock} label="Hours used" testid="fe-hours">{meta.hours_used} hrs</Row>}
                    {meta.drive && <Row Icon={Tractor} label="Drive" testid="fe-drive">{meta.drive}</Row>}
                </div>
                {meta.attachments && (
                    <div className="mt-4">
                        <div className="text-[11px] uppercase tracking-wider font-bold text-aduku-navy/55 mb-1.5">Attachments included</div>
                        <p className="text-sm text-aduku-navy/85 whitespace-pre-wrap" data-testid="fe-attachments">{meta.attachments}</p>
                    </div>
                )}
            </section>
        );
    }

    if (cat === "home-decor") {
        const hasAny = meta.decor_type || meta.style || meta.room || meta.material || meta.dimensions || meta.set_includes;
        if (!hasAny) return null;
        return (
            <section data-testid="home-decor-display" className="bg-white border border-aduku-border rounded-xl p-5 mt-4">
                <div className="flex items-center gap-2 mb-3">
                    <Lamp className="w-4 h-4 text-aduku-navy" />
                    <h3 className="font-display font-bold text-lg text-aduku-navy">Decor details</h3>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                    {meta.decor_type && <Row Icon={Lamp} label="Item" testid="hd-type">{meta.decor_type}</Row>}
                    {meta.style && <Row Icon={Palette} label="Style" testid="hd-style">{meta.style}</Row>}
                    {meta.room && <Row Icon={Home} label="Room" testid="hd-room">{meta.room}</Row>}
                    {meta.material && <Row Icon={Layers} label="Material" testid="hd-material">{meta.material}</Row>}
                    {meta.dimensions && <Row Icon={Ruler} label="Dimensions" testid="hd-dims">{meta.dimensions}</Row>}
                    {meta.set_includes && <Row Icon={Package} label="Includes" testid="hd-includes">{meta.set_includes}</Row>}
                </div>
            </section>
        );
    }

    if (cat === "cement") {
        const hasAny = meta.grade || meta.bag_size_kg || meta.min_order_bags || meta.isi_certified || meta.bulk_delivery;
        if (!hasAny) return null;
        return (
            <section data-testid="cement-display" className="bg-white border border-aduku-border rounded-xl p-5 mt-4">
                <div className="flex items-center gap-2 mb-3">
                    <Layers className="w-4 h-4 text-aduku-navy" />
                    <h3 className="font-display font-bold text-lg text-aduku-navy">Cement specifications</h3>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                    {meta.grade && <Row Icon={Layers} label="Grade" testid="cm-grade">{meta.grade}</Row>}
                    {meta.bag_size_kg && <Row Icon={Package} label="Bag size" testid="cm-bag-size">{meta.bag_size_kg} {meta.bag_size_kg !== "Bulk (no bag)" && "kg"}</Row>}
                    {meta.min_order_bags && <Row Icon={Package} label="Min order" testid="cm-min-order">{meta.min_order_bags} bags / tons</Row>}
                    {meta.isi_certified && <Row Icon={BadgeCheck} label="Certified" testid="cm-isi">{meta.isi_certified}</Row>}
                    {meta.bulk_delivery && <Row Icon={Truck} label="Delivery" testid="cm-bulk-delivery">Bulk truckload available</Row>}
                </div>
            </section>
        );
    }

    if (cat === "paints") {
        const hasAny = meta.paint_type || meta.application || meta.finish || meta.base || meta.volume_litres || meta.coverage_sqft || meta.shade;
        if (!hasAny) return null;
        return (
            <section data-testid="paints-display" className="bg-white border border-aduku-border rounded-xl p-5 mt-4">
                <div className="flex items-center gap-2 mb-3">
                    <PaintBucket className="w-4 h-4 text-aduku-navy" />
                    <h3 className="font-display font-bold text-lg text-aduku-navy">Paint specifications</h3>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                    {meta.paint_type && <Row Icon={PaintBucket} label="Type" testid="pa-type">{meta.paint_type}</Row>}
                    {meta.application && <Row Icon={Home} label="Application" testid="pa-application">{meta.application}</Row>}
                    {meta.finish && <Row Icon={Palette} label="Finish" testid="pa-finish">{meta.finish}</Row>}
                    {meta.base && <Row Icon={Package} label="Base" testid="pa-base">{meta.base}</Row>}
                    {meta.volume_litres && <Row Icon={Package} label="Volume" testid="pa-volume">{meta.volume_litres}</Row>}
                    {meta.coverage_sqft && <Row Icon={Ruler} label="Coverage" testid="pa-coverage">~{meta.coverage_sqft} sq ft/L</Row>}
                    {meta.shade && <Row Icon={Palette} label="Shade" testid="pa-shade">{meta.shade}</Row>}
                </div>
            </section>
        );
    }

    if (cat === "building-materials") {
        const hasAny = meta.material_type || meta.unit || meta.grade_spec || meta.min_qty || meta.site_delivery;
        if (!hasAny) return null;
        return (
            <section data-testid="building-materials-display" className="bg-white border border-aduku-border rounded-xl p-5 mt-4">
                <div className="flex items-center gap-2 mb-3">
                    <Hammer className="w-4 h-4 text-aduku-navy" />
                    <h3 className="font-display font-bold text-lg text-aduku-navy">Material specifications</h3>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                    {meta.material_type && <Row Icon={Hammer} label="Material" testid="bm-material">{meta.material_type}</Row>}
                    {meta.unit && <Row Icon={Package} label="Sale unit" testid="bm-unit">{meta.unit}</Row>}
                    {meta.grade_spec && <Row Icon={BadgeCheck} label="Grade" testid="bm-grade">{meta.grade_spec}</Row>}
                    {meta.min_qty && <Row Icon={Package} label="Min qty" testid="bm-min-qty">{meta.min_qty}</Row>}
                    {meta.site_delivery && <Row Icon={Truck} label="Delivery" testid="bm-delivery">Site delivery available</Row>}
                </div>
            </section>
        );
    }

    if (cat === "jewellery") {
        const hasAny = meta.jewellery_type || meta.metal || meta.purity || meta.weight_g || meta.net_weight_g || meta.hallmark || meta.gemstones || meta.gender || meta.design_style || meta.occasion || meta.making_charges || meta.lifetime_buyback || meta.insured_shipping;
        if (!hasAny) return null;
        return (
            <section data-testid="jewellery-display" className="bg-white border border-aduku-border rounded-xl p-5 mt-4">
                <div className="flex items-center gap-2 mb-3">
                    <Gem className="w-4 h-4 text-aduku-navy" />
                    <h3 className="font-display font-bold text-lg text-aduku-navy">Jewellery details</h3>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                    {meta.jewellery_type && <Row Icon={Gem} label="Type" testid="jw-type">{meta.jewellery_type}</Row>}
                    {meta.metal && <Row Icon={Sparkles} label="Metal" testid="jw-metal">{meta.metal}</Row>}
                    {meta.purity && <Row Icon={BadgeCheck} label="Purity" testid="jw-purity">{meta.purity}</Row>}
                    {meta.weight_g && <Row Icon={Scale} label="Gross wt." testid="jw-weight">{meta.weight_g} g</Row>}
                    {meta.net_weight_g && <Row Icon={Scale} label="Net metal wt." testid="jw-net-weight">{meta.net_weight_g} g</Row>}
                    {meta.hallmark && <Row Icon={Shield} label="Certified" testid="jw-hallmark">{meta.hallmark}</Row>}
                    {meta.gender && <Row Icon={Users} label="For" testid="jw-gender">{meta.gender}</Row>}
                    {meta.design_style && <Row Icon={Palette} label="Style" testid="jw-style">{meta.design_style}</Row>}
                    {meta.occasion && <Row Icon={Calendar} label="Occasion" testid="jw-occasion">{meta.occasion}</Row>}
                    {meta.making_charges && <Row Icon={Hammer} label="Making" testid="jw-making">{meta.making_charges}</Row>}
                    {meta.lifetime_buyback && <Row Icon={BadgeCheck} label="Buy-back" testid="jw-buyback">Lifetime buy-back / exchange</Row>}
                    {meta.insured_shipping && <Row Icon={Truck} label="Shipping" testid="jw-shipping">Insured shipping included</Row>}
                </div>
                {meta.gemstones && (
                    <div className="mt-4">
                        <div className="text-[11px] uppercase tracking-wider font-bold text-aduku-navy/55 mb-1.5">Stones / gems</div>
                        <p className="text-sm text-aduku-navy/85" data-testid="jw-gemstones">{meta.gemstones}</p>
                    </div>
                )}
            </section>
        );
    }

    if (cat === "real-estate") {
        const hasAny = (
            meta.property_type || meta.listing_type || meta.bhk || meta.bathrooms ||
            meta.area_value || meta.furnishing || meta.possession || meta.age ||
            meta.floor || meta.facing || meta.amenities || meta.deposit ||
            meta.maintenance || meta.tenants || meta.parking || meta.rera_approved
        );
        if (!hasAny) return null;
        const areaStr = meta.area_value ? `${meta.area_value}${meta.area_unit ? ` ${meta.area_unit}` : ""}` : null;
        return (
            <section data-testid="real-estate-display" className="bg-white border border-aduku-border rounded-xl p-5 mt-4">
                <div className="flex items-center gap-2 mb-4">
                    <Home className="w-5 h-5 text-aduku-navy" />
                    <h3 className="font-display font-bold text-lg text-aduku-navy">Property details</h3>
                    {meta.listing_type && (
                        <span data-testid="re-listing-badge" className="ml-auto inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-aduku-yellow text-aduku-navy text-[11px] font-bold uppercase tracking-wider">
                            {meta.listing_type}
                        </span>
                    )}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3.5">
                    {meta.property_type && <Row Icon={Home} label="Type" testid="re-property-type">{meta.property_type}</Row>}
                    {meta.bhk && <Row Icon={Layers} label="BHK" testid="re-bhk">{meta.bhk}</Row>}
                    {meta.bathrooms && <Row Icon={Layers} label="Bathrooms" testid="re-bathrooms">{meta.bathrooms}</Row>}
                    {areaStr && <Row Icon={Ruler} label="Area" testid="re-area">{areaStr}</Row>}
                    {meta.furnishing && <Row Icon={Lamp} label="Furnishing" testid="re-furnishing">{meta.furnishing}</Row>}
                    {meta.possession && <Row Icon={Calendar} label="Possession" testid="re-possession">{meta.possession}</Row>}
                    {meta.age && <Row Icon={Clock} label="Age" testid="re-age">{meta.age}</Row>}
                    {meta.floor && <Row Icon={Layers} label="Floor" testid="re-floor">{meta.floor}</Row>}
                    {meta.facing && <Row Icon={Sparkles} label="Facing" testid="re-facing">{meta.facing}</Row>}
                    {meta.parking && <Row Icon={Truck} label="Parking" testid="re-parking">Available</Row>}
                    {meta.rera_approved && <Row Icon={BadgeCheck} label="RERA" testid="re-rera">Approved</Row>}
                    {meta.tenants && <Row Icon={Users} label="Tenants" testid="re-tenants">{meta.tenants}</Row>}
                    {meta.deposit && <Row Icon={Shield} label="Deposit" testid="re-deposit">{meta.deposit}</Row>}
                    {meta.maintenance && <Row Icon={Hammer} label="Maintenance" testid="re-maintenance">{meta.maintenance}</Row>}
                </div>
                {meta.amenities && (
                    <div className="mt-4">
                        <div className="text-[11px] uppercase tracking-wider font-bold text-aduku-navy/55 mb-1.5">Amenities</div>
                        <p className="text-sm text-aduku-navy/85" data-testid="re-amenities">{meta.amenities}</p>
                    </div>
                )}
            </section>
        );
    }

    return null;
}
